// CWC admin Communications page.
//
// Three sections, one page:
//   1. Quick sends      — pending_assessment + pending_video standalone audiences
//                         (no CSV needed, server scans the DB)
//   2. Handshake CSV    — drag-drop CSV, preview bucket counts, per-bucket Send
//   3. Campaign history — last 20 campaigns with status + counts
//
// All sends are async — backend invokes bulk_reminder_sender, returns
// campaign_id immediately, this page shows "Campaign started — refresh history
// in ~2 minutes" rather than polling for progress.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import MailIcon from '@mui/icons-material/Mail';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import AssignmentIcon from '@mui/icons-material/Assignment';
import VideocamIcon from '@mui/icons-material/Videocam';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import ConfirmDialog from '../components/common/ConfirmDialog';
import {
  previewReminderCsv,
  startReminderCampaign,
  listReminderCampaigns,
} from '../services/adminApi';

// Display metadata per bucket — icon + label + color + email subject line.
const BUCKET_META = {
  pending_assessment: {
    label: 'Pending onboarding test',
    icon: <AssignmentIcon />,
    color: '#1e3a8a',
    description: 'They registered on our site but haven\'t taken the 40-question test.',
    subjectPreview: 'Your CWC onboarding test is waiting — 45 minutes to your next step',
  },
  pending_video: {
    label: 'Pending video upload',
    icon: <VideocamIcon />,
    color: '#7c3aed',
    description: 'They passed the test but haven\'t uploaded the 3-5 min video.',
    subjectPreview: 'One step left in your CWC application — the video intro',
  },
  handshake_registration: {
    label: 'Not registered on our site',
    icon: <HowToRegIcon />,
    color: '#2563eb',
    description: 'Handshake-only applicants. They\'ve never reached our registration form.',
    subjectPreview: 'Next step from your CWC Handshake application — finish registering',
  },
};

const STATUS_COLOR = {
  queued: 'default',
  running: 'info',
  uploaded: 'info',
  completed: 'success',
  failed: 'error',
};

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function CommunicationsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // CSV preview state
  const fileInputRef = useRef(null);
  const [csvFileName, setCsvFileName] = useState('');
  const [csvContent, setCsvContent] = useState('');
  const [preview, setPreview] = useState(null); // {csv_total, db_total, counts, samples, ...}
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  // Send state
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingSend, setPendingSend] = useState(null); // {label, audienceType, bucketsToSend, csvContent, planned}

  // Campaign history
  const [campaigns, setCampaigns] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');

  // Toast
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // ---- Effects ----

  const fetchHistory = useCallback(() => {
    setHistoryLoading(true);
    setHistoryError('');
    listReminderCampaigns(20)
      .then((data) => setCampaigns(data.campaigns || []))
      .catch((err) => setHistoryError(err?.response?.data?.error || err.message))
      .finally(() => setHistoryLoading(false));
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // ---- CSV upload + preview ----

  // The picked CSV is cached in localStorage so the preview (and its Send
  // buttons) survives navigation and page reloads — staff shouldn't have to
  // re-pick the file to get back to a send button.
  const CSV_CACHE_KEY = 'cwc_comm_last_csv';

  const handleFilePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);
    setPreview(null);
    setPreviewError('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      setCsvContent(text);
      try {
        localStorage.setItem(CSV_CACHE_KEY, JSON.stringify({ name: file.name, text }));
      } catch (_) { /* quota — preview still works for this visit */ }
      runPreview(text, file.name, { logUpload: true });
    };
    reader.readAsText(file);
  };

  const runPreview = (text, filename, { logUpload = true } = {}) => {
    setPreviewLoading(true);
    setPreviewError('');
    previewReminderCsv(text, { csvFilename: filename || csvFileName || null, logUpload })
      .then((data) => {
        setPreview(data);
        // Fresh uploads are logged as history rows — refresh so the
        // "Last upload" line and history reflect it immediately.
        if (logUpload) fetchHistory();
      })
      .catch((err) => setPreviewError(err?.response?.data?.error || err.message))
      .finally(() => setPreviewLoading(false));
  };

  // Restore the cached CSV on page load. logUpload=false so revisiting the
  // page doesn't log a duplicate "CSV uploaded" history event.
  useEffect(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(CSV_CACHE_KEY) || 'null');
      if (cached?.text && cached?.name) {
        setCsvFileName(cached.name);
        setCsvContent(cached.text);
        runPreview(cached.text, cached.name, { logUpload: false });
      }
    } catch (_) { /* corrupt cache — ignore */ }
    // eslint-disable-next-line
  }, []);

  const clearCsv = () => {
    setCsvFileName('');
    setCsvContent('');
    setPreview(null);
    setPreviewError('');
    try { localStorage.removeItem(CSV_CACHE_KEY); } catch (_) { /* noop */ }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ---- Confirm + send ----

  const openConfirm = ({ label, audienceType, bucketsToSend, csvContent: csv, planned }) => {
    setPendingSend({ label, audienceType, bucketsToSend, csvContent: csv, planned });
    setConfirmOpen(true);
  };

  const doSend = async () => {
    if (!pendingSend) return;
    setSending(true);
    setConfirmOpen(false);
    try {
      const data = await startReminderCampaign({
        audienceType: pendingSend.audienceType,
        csvContent: pendingSend.csvContent || null,
        csvFilename: pendingSend.csvContent ? csvFileName || null : null,
        bucketsToSend: pendingSend.bucketsToSend || null,
      });
      setToast({
        open: true,
        severity: 'success',
        message: `Sending ${pendingSend.planned} email(s) — live progress is shown above Campaign history.`,
      });
      // Refresh history so the new campaign shows up immediately as queued/running
      setTimeout(fetchHistory, 500);
    } catch (err) {
      setToast({
        open: true,
        severity: 'error',
        message: err?.response?.data?.error || err.message || 'Failed to start campaign',
      });
    } finally {
      setSending(false);
      setPendingSend(null);
    }
  };

  // ---- Render ----

  const renderQuickSends = () => (
    <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <MailIcon color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Quick sends
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Send a reminder to <strong>everyone</strong> currently in that status. Applies a 7-day
        cool-off so anyone you already nudged recently is skipped.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Button
          variant="outlined"
          size="large"
          startIcon={<AssignmentIcon />}
          disabled={sending}
          onClick={() =>
            openConfirm({
              label: 'all pending_assessment interns',
              audienceType: 'pending_assessment',
              planned: 'all eligible',
            })
          }
          sx={{ borderColor: BUCKET_META.pending_assessment.color, color: BUCKET_META.pending_assessment.color }}
        >
          Send to pending_assessment
        </Button>
        <Button
          variant="outlined"
          size="large"
          startIcon={<VideocamIcon />}
          disabled={sending}
          onClick={() =>
            openConfirm({
              label: 'all pending_video interns',
              audienceType: 'pending_video',
              planned: 'all eligible',
            })
          }
          sx={{ borderColor: BUCKET_META.pending_video.color, color: BUCKET_META.pending_video.color }}
        >
          Send to pending_video
        </Button>
      </Stack>
    </Paper>
  );

  const renderBucketCard = (templateKey) => {
    if (!preview) return null;
    const meta = BUCKET_META[templateKey];
    const count = preview.counts?.[templateKey] || 0;
    const samples = preview.samples?.[templateKey] || [];

    return (
      <Paper
        key={templateKey}
        sx={{
          p: 2,
          mb: 2,
          borderLeft: `4px solid ${meta.color}`,
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={2}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ color: meta.color }}>{meta.icon}</Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {meta.label}
              </Typography>
              <Chip
                label={`${count} eligible`}
                size="small"
                sx={{
                  bgcolor: count > 0 ? meta.color : 'grey.300',
                  color: count > 0 ? 'white' : 'text.secondary',
                  fontWeight: 600,
                }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {meta.description}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Subject: {meta.subjectPreview}
            </Typography>
            {samples.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                  Sample (first {samples.length}):
                </Typography>
                <Stack spacing={0.25}>
                  {samples.map((s) => (
                    <Typography
                      key={s.email}
                      variant="caption"
                      sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                    >
                      • {s.email} ({s.first_name})
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
          <Button
            variant="contained"
            size="medium"
            startIcon={<SendIcon />}
            disabled={count === 0 || sending}
            onClick={() =>
              openConfirm({
                label: `${count} × ${meta.label}`,
                audienceType: 'handshake_match',
                bucketsToSend: [templateKey],
                csvContent,
                planned: count,
              })
            }
            sx={{
              minWidth: 140,
              bgcolor: meta.color,
              '&:hover': { bgcolor: meta.color, filter: 'brightness(0.9)' },
            }}
          >
            Send {count}
          </Button>
        </Stack>
      </Paper>
    );
  };

  const renderCsvSection = () => (
    <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <UploadFileIcon color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Handshake CSV import
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload a Handshake applicant export. Each row is matched against the interns DB and
        routed to the right reminder template. Anyone past the video review stage is quietly
        skipped.
      </Typography>

      {lastUpload && (
        <Alert severity="info" icon={<UploadFileIcon fontSize="inherit" />} sx={{ mb: 2 }}>
          Last upload: <strong>{lastUpload.csv_filename || 'CSV import'}</strong>
          {lastUpload.csv_row_count != null && <> — {lastUpload.csv_row_count} students</>}
          {' — '}{formatDateTime(lastUpload.created_at)}
          {lastUpload.created_by && lastUpload.created_by !== 'unknown' && <> — by {lastUpload.created_by}</>}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          component="label"
          startIcon={<UploadFileIcon />}
          disabled={previewLoading || sending}
        >
          Choose CSV file
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={handleFilePick}
          />
        </Button>
        {csvFileName && (
          <>
            <Typography variant="body2" sx={{ flex: 1, fontFamily: 'monospace' }}>
              {csvFileName}
            </Typography>
            <Button size="small" onClick={clearCsv} disabled={previewLoading || sending}>
              Clear
            </Button>
          </>
        )}
      </Stack>

      {previewLoading && (
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ my: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Parsing CSV and matching against DB…
          </Typography>
        </Stack>
      )}

      {previewError && (
        <Alert severity="error" sx={{ my: 2 }}>
          {previewError}
        </Alert>
      )}

      {preview && (
        <>
          <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ mb: 2 }}>
            <Stat label="CSV rows" value={preview.csv_total} />
            <Stat label="DB intern records" value={preview.db_total} />
            {preview.duplicates > 0 && <Stat label="Duplicates in CSV" value={preview.duplicates} />}
            <Stat
              label="Past-stage skipped"
              value={preview.counts?.past_stage_skipped || 0}
              hint="In DB but past video review — no email needed"
            />
          </Stack>

          {(preview.cool_off_skipped?.pending_assessment > 0 ||
            preview.cool_off_skipped?.pending_video > 0) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Cool-off (7d) filtered out{' '}
              {preview.cool_off_skipped?.pending_assessment || 0} pending_assessment +{' '}
              {preview.cool_off_skipped?.pending_video || 0} pending_video recipient(s) who were
              already emailed recently.
            </Alert>
          )}

          {renderBucketCard('pending_assessment')}
          {renderBucketCard('pending_video')}
          {renderBucketCard('handshake_registration')}

          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<SendIcon />}
              disabled={
                sending ||
                ((preview.counts?.pending_assessment || 0) +
                  (preview.counts?.pending_video || 0) +
                  (preview.counts?.handshake_registration || 0) ===
                  0)
              }
              onClick={() =>
                openConfirm({
                  label: `all 3 buckets (${
                    (preview.counts?.pending_assessment || 0) +
                    (preview.counts?.pending_video || 0) +
                    (preview.counts?.handshake_registration || 0)
                  } emails total)`,
                  audienceType: 'handshake_match',
                  bucketsToSend: null,
                  csvContent,
                  planned:
                    (preview.counts?.pending_assessment || 0) +
                    (preview.counts?.pending_video || 0) +
                    (preview.counts?.handshake_registration || 0),
                })
              }
            >
              Send all{' '}
              {(preview.counts?.pending_assessment || 0) +
                (preview.counts?.pending_video || 0) +
                (preview.counts?.handshake_registration || 0)}
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );

  // Human labels for the per-template buckets in the breakdown column.
  // NB: avoid the bare word "test" — it reads like a dry run instead of
  // the onboarding-test reminder.
  const BUCKET_LABELS = {
    pending_assessment: 'onboarding-test reminder',
    pending_video: 'video reminder',
    handshake_registration: 'registration invite',
  };

  // Where the campaign's audience came from.
  const sourceOf = (c) => {
    const file = c.csv_filename || 'CSV import';
    const rowCount = c.csv_row_count != null ? ` (${c.csv_row_count} students)` : '';
    if (c.record_type === 'csv_upload') {
      return `CSV uploaded: ${file}${rowCount}`;
    }
    if (c.audience_type === 'handshake_match') {
      return `Handshake send: ${file}${rowCount}`;
    }
    return `Quick send: ${BUCKET_LABELS[c.audience_type] || c.audience_type}`;
  };

  // "test 283/283 · video 20/20" — sent/planned per bucket that had anyone
  // in it. Upload rows (nothing sent yet) show plain per-bucket counts.
  // Falls back to preview counts for queued campaigns the sender hasn't
  // picked up yet.
  const breakdownOf = (c) => {
    const counts = c.counts || {};
    const preview = c.preview_counts || {};
    const isUpload = c.record_type === 'csv_upload';
    const parts = [];
    Object.entries(BUCKET_LABELS).forEach(([key, label]) => {
      const b = counts[key] || {};
      const planned = Number(b.planned ?? preview[key] ?? 0);
      const sent = Number(b.sent ?? 0);
      const suppressed = Number(b.suppressed ?? 0);
      if (planned > 0 || sent > 0) {
        parts.push(isUpload
          ? `${label} ${planned}`
          : `${label} ${sent}/${planned}${suppressed ? ` (${suppressed} suppressed)` : ''}`);
      }
    });
    const skipped = Number(preview.past_stage_skipped ?? 0);
    if (skipped > 0) parts.push(`${skipped} past-stage skipped`);
    return parts.join(' · ') || '—';
  };

  // Most recent CSV upload event, for the "Last upload" line in the
  // import section. Falls back to the newest handshake send that recorded
  // a filename.
  const lastUpload = campaigns.find((c) => c.record_type === 'csv_upload')
    || campaigns.find((c) => c.audience_type === 'handshake_match' && c.csv_filename);

  // Emails delivered so far on an in-flight campaign (sender updates the
  // per-bucket counts every 25 sends; total_sent lands at completion).
  const sentSoFar = (c) => {
    if (c.total_sent != null) return Number(c.total_sent);
    const counts = c.counts || {};
    return Object.values(counts).reduce((sum, b) => sum + Number(b?.sent || 0), 0);
  };

  // The newest campaign still being worked by the sender, if any.
  const activeCampaign = campaigns.find((c) => c.status === 'queued' || c.status === 'running');

  // While a campaign is in flight, poll history every 10s so status and
  // counts move on their own — no manual refresh needed.
  useEffect(() => {
    if (!activeCampaign) return undefined;
    const timer = setInterval(fetchHistory, 10000);
    return () => clearInterval(timer);
  }, [activeCampaign, fetchHistory]);

  // Announce completion: when a campaign we saw as queued/running flips to
  // completed/failed, pop a toast with the final numbers.
  const prevStatusRef = useRef({});
  useEffect(() => {
    const prev = prevStatusRef.current;
    campaigns.forEach((c) => {
      const was = prev[c.campaign_id];
      const wasActive = was === 'queued' || was === 'running';
      if (wasActive && c.status === 'completed') {
        setToast({
          open: true,
          severity: 'success',
          message: `Campaign finished: ${sentSoFar(c)} email(s) sent${Number(c.total_failed) ? `, ${c.total_failed} failed` : ''}.`,
        });
      } else if (wasActive && c.status === 'failed') {
        setToast({
          open: true,
          severity: 'error',
          message: 'Campaign failed — see Campaign history for details.',
        });
      }
    });
    prevStatusRef.current = Object.fromEntries(campaigns.map((c) => [c.campaign_id, c.status]));
    // eslint-disable-next-line
  }, [campaigns]);

  const renderHistory = () => {
    const rows = campaigns.map((c) => ({
      id: c.campaign_id,
      ...c,
    }));
    const columns = [
      {
        field: 'created_at',
        headerName: 'When',
        flex: 1,
        minWidth: 140,
        valueFormatter: ({ value }) => formatDateTime(value),
      },
      {
        field: 'source',
        headerName: 'Source',
        flex: 1.2,
        minWidth: 190,
        valueGetter: ({ row }) => sourceOf(row),
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.6,
        minWidth: 110,
        renderCell: ({ value }) => (
          <Chip label={value || '—'} color={STATUS_COLOR[value] || 'default'} size="small" />
        ),
      },
      {
        field: 'breakdown',
        headerName: 'Emailed (sent/planned)',
        flex: 1.4,
        minWidth: 230,
        sortable: false,
        valueGetter: ({ row }) => breakdownOf(row),
      },
      { field: 'total_sent', headerName: 'Sent', width: 80, type: 'number' },
      { field: 'total_failed', headerName: 'Failed', width: 80, type: 'number' },
      { field: 'created_by', headerName: 'By', flex: 1, minWidth: 160 },
    ];

    return (
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
            Campaign history
          </Typography>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchHistory} disabled={historyLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
        {activeCampaign && (
          <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 2 }}>
            Sending now: <strong>{sourceOf(activeCampaign)}</strong> —{' '}
            {sentSoFar(activeCampaign)} of {activeCampaign.total_planned ?? '?'} emails sent.
            This page updates automatically every 10 seconds.
          </Alert>
        )}
        {historyError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {historyError}
          </Alert>
        )}
        {isMobile ? (
          <Stack spacing={1.5}>
            {campaigns.length === 0 && !historyLoading && (
              <Typography variant="body2" color="text.secondary">
                No campaigns yet.
              </Typography>
            )}
            {campaigns.map((c) => (
              <Paper key={c.campaign_id} variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(c.created_at)}
                  </Typography>
                  <Chip label={c.status || '—'} color={STATUS_COLOR[c.status] || 'default'} size="small" />
                </Stack>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {sourceOf(c)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {breakdownOf(c)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Sent: {c.total_sent ?? '—'} · Failed: {c.total_failed ?? '—'}
                </Typography>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Box sx={{ width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={historyLoading}
              autoHeight
              hideFooterSelectedRowCount
              disableRowSelectionOnClick
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
              }}
              pageSizeOptions={[10, 20]}
            />
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
        Communications
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Send bulk reminder emails from <strong>noreply@cyberwisecorp.com</strong>. Cool-off and
        suppression-list filtering are applied automatically.
      </Typography>

      {renderQuickSends()}
      {renderCsvSection()}
      {renderHistory()}

      <ConfirmDialog
        open={confirmOpen}
        title="Send these emails?"
        message={
          pendingSend
            ? `This will queue ${pendingSend.planned} email(s) to ${pendingSend.label}. Cool-off and suppression filters are already applied. Continue?`
            : ''
        }
        confirmText="Yes, send"
        onConfirm={doSend}
        onCancel={() => {
          setConfirmOpen(false);
          setPendingSend(null);
        }}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function Stat({ label, value, hint }) {
  return (
    <Tooltip title={hint || ''} disableHoverListener={!hint}>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {label}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
          {value}
        </Typography>
      </Box>
    </Tooltip>
  );
}
